/**
 * apiCallLogStore — global ring buffer for outbound API calls.
 *
 * The Debug Panel's "API call log" section subscribes here. To avoid touching
 * the api-client package's exported surface, instrumentation is done by
 * monkey-patching `globalThis.fetch` at module load. Every captured call gets:
 *
 *   { id, method, url, requestHeaders, requestBody, responseStatus,
 *     responseBody, durationMs, timestamp, error? }
 *
 * Bounded to 50 entries; oldest is dropped when full. Subscribers receive the
 * latest snapshot via `subscribe()`.
 *
 * Redaction: this store stores the RAW values; the panel applies the redaction
 * pipeline at clipboard/export time via `redactDebugPayload`. Headers are
 * captured verbatim (auth tokens included) so the redactor can mask them with
 * field-name awareness.
 */

const MAX_ENTRIES = 50;
const MAX_BODY_SAMPLE = 4096; // 4 KB
const MAX_REQ_BODY_SAMPLE = 2048; // 2 KB

export interface ApiCallLogEntry {
  id: number;
  method: string;
  url: string;
  pathname: string;
  requestHeaders: Record<string, string>;
  requestBody?: unknown;
  responseStatus: number;
  responseBody?: unknown;
  responseBodyTruncated?: boolean;
  durationMs: number;
  timestamp: string;
  ok: boolean;
  error?: string;
  /** Function-key fingerprint (first 8 chars). Helps confirm the right key. */
  functionKeyFingerprint?: string;
  /** Officer-code header echoed for audit triage. */
  officerCode?: string;
}

type Listener = (entries: ReadonlyArray<ApiCallLogEntry>) => void;

const buffer: ApiCallLogEntry[] = [];
const listeners = new Set<Listener>();
let nextId = 1;
let installed = false;

function notify(): void {
  const snap = buffer.slice();
  for (const l of listeners) {
    try { l(snap); } catch { /* never propagate */ }
  }
}

function push(entry: ApiCallLogEntry): void {
  buffer.push(entry);
  while (buffer.length > MAX_ENTRIES) buffer.shift();
  notify();
}

function truncate(s: string, max: number): { sample: string; truncated: boolean } {
  if (s.length <= max) return { sample: s, truncated: false };
  return { sample: s.slice(0, max), truncated: true };
}

function safeJsonParse(s: string): unknown {
  try { return JSON.parse(s); } catch { return s; }
}

function headersToObj(h: HeadersInit | undefined): Record<string, string> {
  if (!h) return {};
  const out: Record<string, string> = {};
  if (h instanceof Headers) {
    h.forEach((v, k) => { out[k] = v; });
    return out;
  }
  if (Array.isArray(h)) {
    for (const pair of h) {
      if (pair && pair.length === 2) out[String(pair[0])] = String(pair[1]);
    }
    return out;
  }
  for (const [k, v] of Object.entries(h)) out[k] = String(v);
  return out;
}

function readResponseHeaders(r: Response): Record<string, string> {
  const out: Record<string, string> = {};
  r.headers.forEach((v, k) => { out[k] = v; });
  return out;
}

/**
 * Install the fetch interceptor exactly once. Idempotent — repeated calls are
 * no-ops. Captures both PCB-Function calls and any other host (browsers
 * already include lots of background fetches; we keep them all so devs can
 * see e.g. Teams telemetry calls if useful).
 */
export function installApiCallLog(): void {
  if (installed) return;
  if (typeof globalThis === 'undefined' || typeof globalThis.fetch !== 'function') return;
  installed = true;

  const original = globalThis.fetch.bind(globalThis);

  globalThis.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const start = (typeof performance !== 'undefined' && typeof performance.now === 'function')
      ? performance.now()
      : Date.now();
    const id = nextId++;

    let url = '';
    let method = 'GET';
    let reqHeaders: Record<string, string> = {};
    let reqBody: unknown;

    try {
      if (typeof input === 'string') {
        url = input;
      } else if (input instanceof URL) {
        url = input.toString();
      } else if (input && typeof (input as Request).url === 'string') {
        url = (input as Request).url;
        method = (input as Request).method || method;
        reqHeaders = headersToObj((input as Request).headers);
      }
      if (init) {
        if (init.method) method = init.method;
        if (init.headers) reqHeaders = { ...reqHeaders, ...headersToObj(init.headers) };
        if (init.body !== undefined && init.body !== null) {
          if (typeof init.body === 'string') {
            const t = truncate(init.body, MAX_REQ_BODY_SAMPLE);
            reqBody = t.truncated ? `${t.sample}…[+${init.body.length - MAX_REQ_BODY_SAMPLE}b]` : safeJsonParse(t.sample);
          } else if (init.body instanceof FormData) {
            reqBody = '[FormData]';
          } else if (init.body instanceof Blob) {
            reqBody = `[Blob ${init.body.size}b]`;
          } else if (init.body instanceof ArrayBuffer) {
            reqBody = `[ArrayBuffer ${init.body.byteLength}b]`;
          } else {
            reqBody = '[unknown body]';
          }
        }
      }
    } catch { /* never block the actual fetch */ }

    let pathname = url;
    try {
      const u = new URL(url, typeof window !== 'undefined' ? window.location.href : 'http://localhost');
      pathname = u.pathname + (u.search || '');
    } catch { /* leave url as-is */ }

    const fnKey = reqHeaders['x-functions-key'] || reqHeaders['X-Functions-Key'];
    const officerCode = reqHeaders['x-pcb-officer-code'] || reqHeaders['X-Pcb-Officer-Code'];

    try {
      const response = await original(input as RequestInfo, init);
      const end = (typeof performance !== 'undefined' && typeof performance.now === 'function')
        ? performance.now()
        : Date.now();

      // Clone so we don't consume the body the caller wants to read.
      let responseBody: unknown;
      let truncated = false;
      try {
        const clone = response.clone();
        const ct = clone.headers.get('content-type') ?? '';
        if (ct.includes('application/json') || ct.includes('text/')) {
          const text = await clone.text();
          const t = truncate(text, MAX_BODY_SAMPLE);
          truncated = t.truncated;
          if (ct.includes('application/json')) {
            responseBody = safeJsonParse(t.sample);
          } else {
            responseBody = t.sample;
          }
        } else if (ct) {
          responseBody = `[binary ${ct}]`;
        } else {
          responseBody = '[no body]';
        }
      } catch { responseBody = '[unable to clone response]'; }

      const entry: ApiCallLogEntry = {
        id,
        method,
        url,
        pathname,
        requestHeaders: reqHeaders,
        requestBody: reqBody,
        responseStatus: response.status,
        responseBody,
        responseBodyTruncated: truncated,
        durationMs: Math.round(end - start),
        timestamp: new Date().toISOString(),
        ok: response.ok,
      };
      if (fnKey) entry.functionKeyFingerprint = `${fnKey.slice(0, 8)}…`;
      if (officerCode) entry.officerCode = officerCode;
      // Don't keep raw response headers in the entry to keep it lean (the panel
      // can fetch them lazily via response object if ever needed). However the
      // header types are useful for diagnostics — keep a single Content-Type.
      const respHeaders = readResponseHeaders(response);
      const ct = respHeaders['content-type'];
      if (ct) (entry as ApiCallLogEntry & { responseContentType?: string }).responseContentType = ct;

      push(entry);
      return response;
    } catch (err: unknown) {
      const end = (typeof performance !== 'undefined' && typeof performance.now === 'function')
        ? performance.now()
        : Date.now();
      const message = err instanceof Error ? err.message : String(err);
      const entry: ApiCallLogEntry = {
        id,
        method,
        url,
        pathname,
        requestHeaders: reqHeaders,
        requestBody: reqBody,
        responseStatus: 0,
        durationMs: Math.round(end - start),
        timestamp: new Date().toISOString(),
        ok: false,
        error: message,
      };
      if (fnKey) entry.functionKeyFingerprint = `${fnKey.slice(0, 8)}…`;
      if (officerCode) entry.officerCode = officerCode;
      push(entry);
      throw err;
    }
  };
}

export function getApiCallLogSnapshot(): ReadonlyArray<ApiCallLogEntry> {
  return buffer.slice();
}

export function subscribeApiCallLog(listener: Listener): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function clearApiCallLog(): void {
  buffer.length = 0;
  notify();
}

/** Compute simple percentile latency over the current window. */
export function computeLatencyStats(): { count: number; p50: number; p99: number; mean: number } {
  if (buffer.length === 0) return { count: 0, p50: 0, p99: 0, mean: 0 };
  const sorted = buffer.map((e) => e.durationMs).sort((a, b) => a - b);
  const len = sorted.length;
  const p = (q: number): number => {
    const idx = Math.min(len - 1, Math.max(0, Math.floor(q * (len - 1))));
    return sorted[idx] ?? 0;
  };
  const sum = sorted.reduce((a, b) => a + b, 0);
  return {
    count: len,
    p50: p(0.5),
    p99: p(0.99),
    mean: Math.round(sum / len),
  };
}
